import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, imgSrc } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle2,
  Copy,
  Loader2,
  Instagram,
  ImageOff,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function ymd(date) {
  return date.toISOString().slice(0, 10);
}

export default function CalendarView() {
  const { activeId, activeClient } = useClient();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [errorPost, setErrorPost] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const igExp = activeClient?.ig_token_expires_at;
  const igDaysLeft = igExp
    ? Math.floor((new Date(igExp).getTime() - Date.now()) / 86400000)
    : null;

  const load = async () => {
    if (!activeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/posts", { params: { client_id: activeId } });
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const scheduled = posts.filter((p) => p.scheduled_for);
  const byDay = {};
  scheduled.forEach((p) => {
    const key = p.scheduled_for.slice(0, 10);
    (byDay[key] = byDay[key] || []).push(p);
  });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const del = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Post supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPublished = async (id) => {
    try {
      const { data } = await api.put(`/posts/${id}`, { status: "published" });
      setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success("Marqué comme publié");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const publishInsta = async (id) => {
    try {
      const { data } = await api.post(`/posts/${id}/publish-instagram`);
      setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success("Publié sur Instagram");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const retryPublish = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/posts/${id}/retry-publish`);
      setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
      setErrorPost(null);
      toast.success("Republication relancée — elle partira dans quelques secondes");
      setTimeout(load, 9000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setRetrying(null);
  };

  const copyPost = async (p) => {
    const text =
      p.caption + (p.hashtags?.length ? "\n\n" + p.hashtags.map((h) => `#${h}`).join(" ") : "");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Légende copiée");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      toast[ok ? "success" : "error"](ok ? "Légende copiée" : "Copie impossible");
    }
  };

  const dayPosts = selectedDay ? byDay[selectedDay] || [] : [];
  const todayKey = ymd(new Date());

  return (
    <>
      <PageHeader overline="Planification" title="Calendrier">
        <div className="flex items-center gap-1">
          <button
            data-testid="prev-month"
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            className="p-2 border border-white/15 rounded-sm hover:bg-white/[0.05] transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-4 text-sm font-bold uppercase tracking-wider min-w-[140px] text-center">
            {MONTHS[month]} {year}
          </span>
          <button
            data-testid="next-month"
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            className="p-2 border border-white/15 rounded-sm hover:bg-white/[0.05] transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </PageHeader>

      {activeClient && (!activeClient.ig_connected || activeClient.ig_needs_reconnect) && (
        <div
          data-testid="calendar-ig-warning"
          className="mx-6 lg:mx-8 mt-4 border border-red-500/40 bg-red-500/10 rounded-sm px-4 py-3 flex items-start gap-3"
        >
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-bold text-red-300">
              {activeClient.ig_needs_reconnect
                ? "Connexion Instagram expirée — les publications automatiques échouent"
                : "Instagram n'est pas connecté — aucun post ne pourra être publié automatiquement"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Tes posts restent planifiés mais ne partiront pas tant que le compte Instagram de{" "}
              <b>{activeClient.name}</b> n'est pas {activeClient.ig_needs_reconnect ? "reconnecté" : "connecté"}.
            </p>
          </div>
          <Link
            to="/parametres"
            data-testid="calendar-connect-cta"
            className="shrink-0 bg-[#1877F2] text-white rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#1877F2]/85 transition-colors"
          >
            {activeClient.ig_needs_reconnect ? "Reconnecter" : "Connecter"}
          </Link>
        </div>
      )}

      {activeClient && activeClient.ig_connected && !activeClient.ig_needs_reconnect && igDaysLeft != null && igDaysLeft <= 7 && (
        <div
          data-testid="calendar-ig-expiring"
          className="mx-6 lg:mx-8 mt-4 border border-amber-500/40 bg-amber-500/10 rounded-sm px-4 py-3 flex items-start gap-3"
        >
          <Clock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-bold text-amber-300">
              Connexion Instagram bientôt expirée ({igDaysLeft <= 0 ? "aujourd'hui" : `dans ${igDaysLeft} j`})
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Reconnecte le compte de <b>{activeClient.name}</b> maintenant pour éviter toute interruption des publications automatiques.
            </p>
          </div>
          <Link
            to="/parametres"
            data-testid="calendar-renew-cta"
            className="shrink-0 border border-amber-500/50 text-amber-300 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-amber-500/10 transition-colors"
          >
            Reconnecter
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* calendar grid */}
        <div className="lg:col-span-8 p-6 lg:p-8 lg:border-r border-white/10">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="border-t border-l border-white/10" data-testid="calendar-grid">
              <div className="grid grid-cols-7">
                {DAYS.map((d) => (
                  <div
                    key={d}
                    className="border-r border-b border-white/10 py-2 text-center text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground"
                  >
                    {d}
                  </div>
                ))}
                {cells.map((date, i) => {
                  if (!date)
                    return (
                      <div key={i} className="border-r border-b border-white/10 aspect-square bg-[#080808]" />
                    );
                  const key = ymd(date);
                  const items = byDay[key] || [];
                  const isToday = key === todayKey;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDay(key)}
                      data-testid={`day-${key}`}
                      className={`border-r border-b border-white/10 aspect-square p-1.5 text-left flex flex-col transition-colors hover:bg-white/[0.03] ${
                        selectedDay === key ? "bg-white/[0.05]" : ""
                      }`}
                    >
                      <span
                        className={`text-xs font-mono ${
                          isToday ? "text-accent font-bold" : "text-muted-foreground"
                        }`}
                      >
                        {date.getDate()}
                      </span>
                      <div className="flex-1 flex flex-col gap-1 mt-1 overflow-hidden">
                        {items.slice(0, 2).map((p) => (
                          <div
                            key={p.id}
                            className="h-1.5 w-full bg-accent/70 rounded-full"
                            title={p.product_title}
                          />
                        ))}
                        {items.length > 2 && (
                          <span className="text-[9px] text-muted-foreground">
                            +{items.length - 2}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* day detail / list */}
        <div className="lg:col-span-4 p-6 lg:p-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
            {selectedDay
              ? `Posts du ${selectedDay.split("-").reverse().join("/")}`
              : "Tous les posts planifiés"}
          </p>
          <div className="space-y-3" data-testid="day-posts">
            {(selectedDay ? dayPosts : scheduled).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun post planifié{selectedDay ? " ce jour" : ""}.
              </p>
            ) : (
              (selectedDay ? dayPosts : scheduled).map((p) => (
                <div
                  key={p.id}
                  data-testid={`sched-post-${p.id}`}
                  className="border border-white/10 rounded-sm bg-secondary overflow-hidden"
                >
                  <div className="flex">
                    <div className="w-20 shrink-0 bg-[#0d0d0d] flex items-center justify-center">
                      {p.image_url ? (
                        <img src={imgSrc(p.image_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <ImageOff className="h-5 w-5 text-white/20" />
                      )}
                    </div>
                    <div className="p-3 flex-1 min-w-0">
                      <p className="font-mono text-[10px] text-accent mb-1">
                        {p.scheduled_for
                          ? new Date(p.scheduled_for).toLocaleString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </p>
                      <span
                        data-testid={`status-${p.id}`}
                        className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm mb-1 ${
                          p.status === "published"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : p.status === "failed" || p.status === "error"
                            ? "bg-red-500/20 text-red-300"
                            : p.status === "cancelled"
                            ? "bg-white/10 text-muted-foreground line-through"
                            : p.status === "publishing" || p.status === "queued"
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-white/10 text-muted-foreground"
                        }`}
                      >
                        {p.status === "published"
                          ? p.published_via === "instagram"
                            ? "Publié sur Instagram"
                            : "Publié"
                          : p.status === "failed" || p.status === "error"
                          ? "Échec de publication"
                          : p.status === "cancelled"
                          ? "Annulé"
                          : p.status === "queued"
                          ? "En file d'attente…"
                          : p.status === "publishing"
                          ? "Publication en cours…"
                          : "Programmé"}
                      </span>
                      <p className="text-xs leading-snug line-clamp-2 text-muted-foreground">
                        {p.caption || p.product_title}
                      </p>
                    </div>
                  </div>
                  <div className="flex border-t border-white/10">
                    <button
                      onClick={() => copyPost(p)}
                      data-testid={`copy-${p.id}`}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
                    >
                      <Copy className="h-3 w-3" /> Copier
                    </button>
                    {(p.status === "failed" || p.status === "error") && (
                      <button
                        onClick={() => setErrorPost(p)}
                        data-testid={`view-error-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-colors border-l border-white/10"
                      >
                        <AlertTriangle className="h-3 w-3" /> Voir l'erreur
                      </button>
                    )}
                    {(p.status === "scheduled" || p.status === "failed" || p.status === "error" || !p.status) &&
                      (activeClient?.ig_connected ? (
                      <button
                        onClick={() => (p.status === "failed" || p.status === "error") ? retryPublish(p.id) : publishInsta(p.id)}
                        data-testid={`insta-${p.id}`}
                        disabled={retrying === p.id}
                        className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-accent hover:bg-white/[0.04] transition-colors border-l border-white/10"
                      >
                        {retrying === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Instagram className="h-3 w-3" />}
                        {(p.status === "failed" || p.status === "error") ? "Réessayer" : "Insta"}
                      </button>
                    ) : (
                      <button
                        onClick={() => markPublished(p.id)}
                        data-testid={`publish-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-emerald-400 hover:bg-white/[0.04] transition-colors border-l border-white/10"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Publié
                      </button>
                    ))}
                    <button
                      onClick={() => del(p.id)}
                      data-testid={`delete-${p.id}`}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-accent hover:bg-white/[0.04] transition-colors border-l border-white/10"
                    >
                      <Trash2 className="h-3 w-3" /> Suppr.
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {errorPost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setErrorPost(null)}
          data-testid="publish-error-modal"
        >
          <div
            className="bg-secondary border border-red-500/30 rounded-sm max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-white/10 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <h3 className="font-heading font-bold text-base">Détail de l'échec de publication</h3>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Explication</div>
                <p className="text-red-200" data-testid="publish-error-message">
                  {errorPost.publish_error || errorPost.last_error || "Erreur inconnue."}
                </p>
              </div>
              {errorPost.publish_diagnostics && (() => {
                const d = errorPost.publish_diagnostics;
                const meta = d.create_container || d.publish || d.token_check || {};
                const rows = [];
                if (meta.http_status) rows.push(["HTTP", meta.http_status]);
                if (meta.code !== undefined) rows.push(["Code Meta", meta.code]);
                if (meta.subcode !== undefined) rows.push(["Sous-code", meta.subcode]);
                if (meta.type) rows.push(["Type", meta.type]);
                if (meta.message) rows.push(["Message Meta", meta.message]);
                if (meta.fbtrace_id) rows.push(["fbtrace_id", meta.fbtrace_id]);
                if (d.creation_id) rows.push(["creation_id", d.creation_id]);
                if (d.container_status?.status_code) rows.push(["Statut conteneur", d.container_status.status_code]);
                if (d.media_url_check) rows.push(["Image accessible", d.media_url_check.accessible ? `oui (HTTP ${d.media_url_check.http_status})` : "NON"]);
                if (d.published_media_id) rows.push(["ID média publié", d.published_media_id]);
                return rows.length ? (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Réponse technique de Meta</div>
                    <div className="border border-white/10 rounded-sm divide-y divide-white/5 font-mono text-xs">
                      {rows.map(([k, v]) => (
                        <div key={k} className="flex gap-2 px-3 py-1.5">
                          <span className="text-muted-foreground w-32 shrink-0">{k}</span>
                          <span className="break-all">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
              {!errorPost.publish_diagnostics && (
                <p className="text-xs text-muted-foreground">
                  Aucun diagnostic détaillé pour cet échec (échec antérieur à la mise à jour, ou avant connexion Instagram).
                </p>
              )}
            </div>
            <div className="p-5 border-t border-white/10 flex gap-2">
              <button
                onClick={() => setErrorPost(null)}
                data-testid="close-error-modal"
                className="flex-1 py-2 text-xs font-bold uppercase tracking-wider border border-white/20 rounded-sm hover:bg-white/5 transition-colors"
              >
                Fermer
              </button>
              {activeClient?.ig_connected && (
                <button
                  onClick={() => retryPublish(errorPost.id)}
                  disabled={retrying === errorPost.id}
                  data-testid="retry-from-modal"
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-2"
                >
                  {retrying === errorPost.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Instagram className="h-3.5 w-3.5" />}
                  Réessayer la publication
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
